import { ReactNode, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Box, CircularProgress, Paper, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { RegistryHistory as Entry, TablePrEvent, lhRegistryApiRef } from './api';
import { commitErrorCaption } from './manifestLabel';
import { Pill, TicketLink, fmtAt, shortActor } from './ui';

const EVENT_LABEL: Record<string, string> = {
  ticket_waiting: 'PR 대기 등록', enqueue_skipped: 'manifest 대기열 건너뜀', bundle_confirmed: '묶음 확정', commit: '커밋', commit_error: '커밋 중 오류',
  pr_opened: 'PR 생성', ci: 'CI 변화', merged: 'main 머지', promote_posted: '/promote 요청', promote_pr_opened: 'prod PR 생성', prod_merged: 'prod 반영',
  promote_blocked: 'promote 차단', foreign_commit: '외부 커밋', excluded: '묶음에서 제외', closed: 'PR 닫힘', bot_linked: 'BOT PR 연결', failed: '실패',
};
const show = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '∅';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
};
const MONO = "'Roboto Mono', monospace";

type Item = { at: number; key: string; badge: ReactNode; kind: string; time: string; ticket?: string | null; actor: string; reason?: string; diffs: [string, unknown, unknown][]; notes: string[] };

/** 위키식 변경 이력 — 레지스트리 version 과 manifest PR 이벤트를 최신순으로 섞는다 */
export const RegistryHistory = ({ tableId }: { tableId: number }) => {
  const api = useApi(lhRegistryApiRef);
  const [entries, setEntries] = useState<Entry[]>();
  const [events, setEvents] = useState<TablePrEvent[]>([]);
  const [statuses, setStatuses] = useState<Record<string, { status: string }>>({});
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    api
      .history(tableId)
      .then(async h => {
        if (cancelled) return;
        setEntries(h);
        // manifest 이벤트는 보조 정보 — GitHub 설정이 없거나(503) 실패하면 레지스트리 이력만 보인다
        api.tablePrs(tableId).then(r => !cancelled && setEvents(r.events)).catch(() => undefined);
        const keys = [...new Set(h.map(e => e.ticketKey).filter((k): k is string => !!k))].slice(0, 50);
        const s = await api.ticketStatuses(keys);
        if (!cancelled) setStatuses(s);
      })
      .catch(e => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [api, tableId]);

  const body = () => {
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!entries) return <CircularProgress size={20} />;
    const items: Item[] = [
      ...entries.map(e => ({
        at: new Date(e.changedAt).getTime(), key: `v-${e.version}`, badge: <Pill tone="solid">v{e.version}</Pill>,
        kind: e.oldValues === null ? '등록' : '수정', time: fmtAt(e.changedAt), ticket: e.ticketKey,
        actor: `${shortActor(e.changedBy)}${e.requestedBy ? ` · 요청 ${shortActor(e.requestedBy)}` : ''}`, reason: e.reason,
        diffs: [
          ...Object.keys(e.newValues).map(k => [k, e.oldValues === null ? undefined : e.oldValues[k], e.newValues[k]] as [string, unknown, unknown]),
          ...(e.columns ?? []).map((c): [string, unknown, unknown] => {
            if (c.action === 'add') return ['column +', undefined, c.columnName];
            if (c.action === 'drop') return ['column −', c.columnName, undefined];
            return [`column ${c.columnName}`, c.old, c.new];
          }),
        ],
        notes: [],
      })),
      ...events.map(ev => ({
        at: new Date(ev.at).getTime(), key: `ev-${ev.id}`, badge: <Pill tone="info">PR</Pill>,
        kind: ev.kind === 'commit_error' ? commitErrorCaption(ev.detail?.errorCode) : EVENT_LABEL[ev.kind] ?? ev.kind, time: fmtAt(ev.at), actor: shortActor(ev.actor),
        diffs: ev.from && ev.to ? [['status', ev.from, ev.to] as [string, unknown, unknown]] : [],
        // promote 계열 이벤트의 상세(PR 번호 등)는 서버가 admin 에게만 준다
        notes: [typeof ev.detail?.prNumber === 'number' ? `PR #${ev.detail.prNumber}` : '', typeof ev.detail?.errorCode === 'string' ? ev.detail.errorCode : ''].filter(Boolean),
      })),
    ].sort((a, b) => b.at - a.at);

    return (
      <Box component="ol" m={0} p={0} display="flex" flexDirection="column" style={{ gap: 14, listStyle: 'none' }}>
        {items.map(h => (
          <Box component="li" key={h.key} display="flex" style={{ gap: 12 }}>
            <Box flexShrink={0} width={44} display="flex" justifyContent="center">{h.badge}</Box>
            <Box minWidth={0} flex={1}>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="body2"><b>{h.kind}</b></Typography>
                <Typography variant="caption" color="textSecondary">{h.time}</Typography>
                {h.ticket && <Pill tone="neutral"><TicketLink ticketKey={h.ticket} /> · {statuses[h.ticket]?.status ?? '—'}</Pill>}
              </Box>
              <Typography variant="caption" component="div" color="textSecondary">{h.actor}</Typography>
              {h.reason && <Typography variant="body2">“{h.reason}”</Typography>}
              {h.diffs.map(([k, o, n], i) => (
                <Typography key={i} variant="caption" component="div" style={{ fontFamily: MONO }}>
                  <span style={{ opacity: 0.7 }}>{k}</span> {o === undefined ? '—' : show(o)} → {n === undefined ? '—' : show(n)}
                </Typography>
              ))}
              {h.notes.map(x => <Typography key={x} variant="caption" component="div">{x}</Typography>)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Paper variant="outlined" component="section" aria-label="변경 이력">
      <Box p={2.5}>
        <Typography variant="subtitle1">변경 이력</Typography>
        <Typography variant="caption" component="div" color="textSecondary" gutterBottom>최신순 · lh_collect_table_hist + lh_manifest_event(PR)</Typography>
        {body()}
      </Box>
    </Paper>
  );
};
